import React from "react";
import "./change-reason-banner.scss";

export type ChangeReasonBannerVariant =
  | "warning"
  | "info"
  | "success"
  | "error"
  | "neutral";

export interface ChangeReasonBannerColors {
  /** Container background color. */
  backgroundColor?: string;
  /** Left border accent color (also used for the optional icon). */
  accentColor?: string;
  /** Color for the uppercase label text. */
  labelColor?: string;
  /** Color for the reason / description text. */
  textColor?: string;
}

export interface ChangeReasonBannerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /**
   * Uppercase label shown before the reason (e.g. "ANSWER CHANGED:").
   * Accepts a string or any React node. The trailing colon is only auto-appended for string labels;
   * pass `hideLabelColon` or include your own punctuation for ReactNode labels.
   */
  label: React.ReactNode;
  /** Reason / description text (e.g. "Missing information in document"). */
  reason: React.ReactNode;
  /** Visual variant controlling the color palette. Defaults to "warning". */
  variant?: ChangeReasonBannerVariant;
  /** Optional leading icon rendered before the label. */
  icon?: React.ReactNode;
  /** Icon size in pixels. Defaults to 14. */
  iconSize?: number;
  /** Optional trailing action node (e.g. a button or close icon). */
  action?: React.ReactNode;
  /** Per-token color overrides. Each value overrides the variant default. */
  colors?: ChangeReasonBannerColors;
  /** When true, allows the reason text to wrap onto multiple lines. Defaults to false. */
  multiline?: boolean;
  /** When true, hides the trailing colon automatically appended to the label. Defaults to false. */
  hideLabelColon?: boolean;
  /** Additional CSS class names applied to the root container. */
  className?: string;
}

const formatLabel = (
  label: React.ReactNode,
  hideColon: boolean
): React.ReactNode => {
  if (typeof label !== "string") return label;
  const trimmed = label.trim();
  if (hideColon) return trimmed;
  return trimmed.endsWith(":") ? trimmed : `${trimmed}:`;
};

export const ChangeReasonBanner: React.FC<ChangeReasonBannerProps> = ({
  label,
  reason,
  variant = "warning",
  icon,
  iconSize = 14,
  action,
  colors,
  multiline = false,
  hideLabelColon = false,
  className = "",
  style,
  ...rest
}) => {
  const cssVarStyle: React.CSSProperties = {
    ...(colors?.backgroundColor
      ? ({ ["--change-reason-banner-bg" as string]: colors.backgroundColor } as React.CSSProperties)
      : {}),
    ...(colors?.accentColor
      ? ({ ["--change-reason-banner-accent" as string]: colors.accentColor } as React.CSSProperties)
      : {}),
    ...(colors?.labelColor
      ? ({ ["--change-reason-banner-label" as string]: colors.labelColor } as React.CSSProperties)
      : {}),
    ...(colors?.textColor
      ? ({ ["--change-reason-banner-text" as string]: colors.textColor } as React.CSSProperties)
      : {}),
    ...style,
  };

  return (
    <div
      role="status"
      className={`change-reason-banner change-reason-banner--${variant} ${className}`.trim()}
      style={cssVarStyle}
      {...rest}
    >
      <div
        className={`change-reason-banner__row ${
          multiline ? "change-reason-banner__row--multiline" : ""
        }`.trim()}
      >
        {icon && (
          <span
            className="change-reason-banner__icon"
            style={{ width: iconSize, height: iconSize }}
            aria-hidden="true"
          >
            {icon}
          </span>
        )}
        <span className="change-reason-banner__label">
          {formatLabel(label, hideLabelColon)}
        </span>
        <span className="change-reason-banner__reason">{reason}</span>
        {action && <span className="change-reason-banner__action">{action}</span>}
      </div>
    </div>
  );
};

export default ChangeReasonBanner;
